<template>
  <component
    :is="component"
    v-model="dialogValue"
    :fullscreen="fullScreen"
    inset
  >
    <v-card
      class="d-flex flex-column overflow-hidden rounded-b-0 white"
      :class="fullScreen ? 'rounded-0' : 'rounded-t-xl'"
      :style="{ height: $isMobile ? (fullScreen ? '100vh' : '85vh') : '85vh' }"
    >
      <v-card-title class="justify-space-between white">
        <SinoaTitle />
        <SinoaActions
          :full-screen="fullScreen"
          :showing-history="historyDrawer"
          @toggle-history="toggleHistoryDrawer"
          @toggle-fullscreen="toggleFullscreen"
          @close-dialog="closeDialog"
        />
      </v-card-title>

      <v-divider />

      <v-row
        no-gutters
        class="flex-grow-1 fill-height overflow-hidden flex-min-0"
      >
        <v-col
          v-if="historyDrawer"
          :cols="$isMobile ? 12 : 4"
          class="d-flex flex-column fill-height overflow-hidden flex-min-0"
        >
          <template v-if="!$isMobile">
            <BreadcrumbGrainFoxHistory
              @back="historyDrawer = false"
            />
          </template>

          <ChatHistoryDrawer
            :history-drawer="historyDrawer"
            @show-answer="handleShowAnswer"
          />
        </v-col>

        <v-col
          :cols="historyDrawer && !$isMobile ? 8 : 12"
          class="d-flex flex-column fill-height overflow-hidden flex-min-0"
        >
          <div class="d-flex flex-column fill-height overflow-hidden flex-min-0">
            <template v-if="!hasRealMessages">
              <v-card-text
                ref="chatContainer"
                class="chat-container overflow-y-auto flex-grow-1 flex-min-0"
                :class="[
                  $isMobile ? 'px-6' : 'px-8',
                  { 'full-screen': fullScreen }
                ]"
              >
                <Paywall v-if="showPaywall" />
                <div
                  v-else
                  class="d-flex flex-column justify-center align-center text-center"
                >
                  <SinoaWelcome />
                </div>
              </v-card-text>
            </template>

            <template v-else>
              <v-card-text
                ref="chatContainer"
                class="chat-container overflow-y-auto flex-grow-1 flex-min-0"
                :class="{ 'full-screen': fullScreen}"
              >
                <ChatMessages
                  class="mt-4"
                  :full-screen="fullScreen"
                  @scroll-bottom="scrollToBottom"
                  @close-history="historyDrawer = false"
                  @followup-click="onFollowUp"
                />
              </v-card-text>
            </template>

            <v-card-text
              class="py-2 mt-auto"
              :class="$isMobile ? 'px-6' : 'px-8'"
            >
              <v-divider class="mb-4" />
              <FrequentQuestionsCarousel
                class="mb-4"
                @faq-click="handleShowAnswer"
              />
              <SubscriptionChatUsage
                :key="`chat-usage-${fullScreen}`"
                :full-screen="fullScreen"
                @buy-more-questions="handleBuyMoreQuestions"
              />
            </v-card-text>

            <v-card-actions class="px-6 pb-6 pt-0">
              <CyclicPlaceholder
                v-slot="{ text }"
                class="d-flex flex-grow-1"
              >
                <div
                  style="position: relative;"
                  class="d-flex flex-grow-1"
                >
                  <v-text-field
                    ref="inputField"
                    v-model="userInput"
                    :placeholder="isStreaming ? '' : text"
                    hide-details
                    outlined
                    dense
                    rounded
                    :disabled="isSinoaLoading || isLimitReached || isStreaming"
                    append-icon="mdi-arrow-right-circle"
                    :color="COLORS.grainfoxAIButton"
                    @keyup.enter="sendUserMessage"
                    @click:append="sendUserMessage"
                  />
                  <div
                    v-if="isStreaming"
                    class="streaming-placeholder d-flex"
                  >
                    <span /><span /><span />
                  </div>
                </div>
              </CyclicPlaceholder>
            </v-card-actions>
          </div>
        </v-col>
      </v-row>
    </v-card>
  </component>
</template>

<script>
import { COLORS } from '@/helpers/colors';
import VBottomSheet from 'vuetify/lib/components/VBottomSheet';
import FrequentQuestionsCarousel from './FrequentQuestionsCarousel.vue';
import SubscriptionChatUsage from './SubscriptionChatUsage.vue';
import SinoaTitle from './SinoaTitle.vue';
import SinoaWelcome from './SinoaWelcome.vue';
import ChatMessages from './ChatMessages.vue';
import ChatHistoryDrawer from './ChatHistoryDrawer.vue';
import { mapState, mapActions, mapGetters, mapMutations } from 'vuex';
import BreadcrumbGrainFoxHistory from './BreadcrumbGrainFoxHistory.vue';
import CyclicPlaceholder from './CyclicPlaceholder.vue';
import Paywall from './Paywall.vue';
import { datadogRum } from '@datadog/browser-rum';
import SinoaActions from './SinoaActions.vue';

export default {
  name: 'GrainFoxAssistantDialog',

  components: {
    VBottomSheet,
    FrequentQuestionsCarousel,
    SubscriptionChatUsage,
    SinoaTitle,
    SinoaWelcome,
    ChatMessages,
    ChatHistoryDrawer,
    BreadcrumbGrainFoxHistory,
    CyclicPlaceholder,
    Paywall,
    SinoaActions,
  },

  data() {
    return {
      fullScreen: false,
      historyDrawer: false,
      COLORS,
      userInput: '',
    };
  },

  computed: {
    ...mapState('chatbot', ['isGrainFoxAssistantDialogOpen', 'messages', 'showPaywall']),
    ...mapGetters('chatbot', ['hasRealMessages', 'isLimitReached', 'isSinoaLoading', 'isStreaming']),

    dialogValue: {
      get() {
        return this.isGrainFoxAssistantDialogOpen;
      },
      set(val) {
        this.setGrainFoxAssistantDialogOpen(val);
      },
    },

    component() {
      return this.fullScreen ? 'v-dialog' : 'v-bottom-sheet';
    },
  },

  mounted() {
    this.updateFaqVisibility();
  },

  watch: {
    dialogValue(newVal) {
      if (newVal) {
        this.fetchDeepDiveSettings();
        if (!this.showPaywall && !this.$isMobile) {
          this.$nextTick(() => this.focusInput());
        }
      }
    },

    isSinoaLoading(newVal) {
      if (!newVal && !this.$isMobile) {
        this.$nextTick(() => this.focusInput());
      }
    },

    messages: {
      handler() {
        this.$nextTick(() => {
          this.scrollToBottom();
          if (!this.$isMobile) {
            this.focusInput();
          }
        });
      },
      deep: false,
    },

    hasRealMessages() {
      this.$nextTick(() => this.updateFaqVisibility());
    },

    showPaywall() {
      this.$nextTick(() => {
        this.updateFaqVisibility();
        if (this.$isMobile) this.fullScreen = true;
        this.scrollToBottom();
      });
    },
  },

  methods: {
    ...mapActions('chatbot', ['sendMessage', 'showStoredAnswer', 'fetchDeepDiveSettings']),
    ...mapMutations('chatbot', ['setGrainFoxAssistantDialogOpen', 'setFollowUpQuestions', 'setShowFaqs', 'setShowPaywall']),

    updateFaqVisibility() {
      const shouldShow = this.$isMobile
        ? this.showPaywall
        : (!this.hasRealMessages || this.showPaywall);

      this.setShowFaqs(shouldShow);
    },

    async sendUserMessage() {
      const question = this.userInput.trim();
      if (!question) return;

      this.userInput = '';
      await this.sendMessage({ question });
      this.updateFaqVisibility();
      datadogRum.addAction('Sinoa: Send Message', {
        source: 'GrainFoxAssistantDialog',
        isMobile: this.$isMobile ?? null,
      });
    },

    closeDialog() {
      this.fullScreen = false;
      this.historyDrawer = false;
      this.setGrainFoxAssistantDialogOpen(false);
    },

    toggleFullscreen() {
      this.fullScreen = !this.fullScreen;
      if (!this.fullScreen) this.historyDrawer = false;
    },

    toggleHistoryDrawer() {
      this.fullScreen = true;
      this.historyDrawer = !this.historyDrawer;
    },

    scrollToBottom() {
      if (this.$refs.chatContainer) {
        this.$nextTick(() => {
          this.$refs.chatContainer.scrollTop = this.$refs.chatContainer.scrollHeight;
        });
      }
    },

    focusInput() {
      this.$nextTick(() => {
        this.$refs.inputField?.focus?.();
      });
    },

    handleShowAnswer(item) {
      this.showStoredAnswer(item);
      if (this.$isMobile) {
        this.historyDrawer = false;
      }
    },

    async onFollowUp(question) {
      this.setFollowUpQuestions([]);
      await this.sendMessage({ question });
    },

    handleBuyMoreQuestions() {
      this.setShowPaywall(true);
      this.updateFaqVisibility();
    },
  },
};
</script>

<style lang="scss" scoped>
@use "@/colors/colors.scss";

.flex-min-0 {
  min-height: 0;
}

.chat-container {
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  min-height: 0;
  flex: 1 1 auto;

  @media (max-width: 600px) {
    &.full-screen {
      max-height: calc(100vh - 200px);
    }
  }
}

::v-deep .v-input__append-inner .v-icon {
  transform: translateX(16px);
}

::v-deep .v-dialog.v-dialog--fullscreen .v-card {
  max-width: 60vw !important;
  @media (max-width: 600px) {
    max-width: 100vw !important;
  }
}

.streaming-placeholder {
  position: absolute;
  top: 50%;
  left: 5%;
  transform: translateY(-50%);
  gap: 4px;
  pointer-events: none;
}

.streaming-placeholder span {
  width: 6px;
  height: 6px;
  background: colors.$primaryGrey;
  border-radius: 50%;
  animation: blink 1.4s infinite both;
}

.streaming-placeholder span:nth-child(2) {
  animation-delay: 0.2s;
}

.streaming-placeholder span:nth-child(3) {
  animation-delay: 0.4s;
}

@keyframes blink {
  0% { opacity: 0.2; transform: scale(1); }
  20% { opacity: 1; transform: scale(1.3); }
  100% { opacity: 0.2; transform: scale(1); }
}

</style>
